import { UseFormReturn, useFieldArray } from "react-hook-form";
import { Plus, X } from "lucide-react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CampaignFormData } from "./campaignFormTypes";
import { CREATOR_PROFILE_VARS } from "./formulaUtils";

interface CustomFormulaFieldsProps {
  form: UseFormReturn<CampaignFormData>;
  /** Path to the custom_formula object, e.g. "tiers.0.components.0.custom_formula" */
  basePath: string;
}

export default function CustomFormulaFields({ form, basePath }: CustomFormulaFieldsProps) {
  const constantsPath = `${basePath}.constants` as any;
  const expressionPath = `${basePath}.expression` as any;

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: constantsPath,
  });

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name={expressionPath}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Formula Expression</FormLabel>
            <div className="flex flex-row items-center">
              <span className="text-sm text-nowrap border rounded-l-lg border-input bg-muted px-2.5 py-1.75 border-r-0">
                Max Rate =
              </span>
              <FormControl>
                <Input
                  className="rounded-l-none"
                  placeholder="e.g. avg_views * CTR * CVR * LTV / ROAS"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
            </div>
            <FormDescription>
              <span>
                Use constants you define below, creator profile variables
                {CREATOR_PROFILE_VARS.map((variable) => (
                  <span key={variable} className="mx-0.5 select-all rounded-sm bg-primary/10 px-1 font-mono text-primary">
                    {variable}
                  </span>
                ))}
                , and numeric CRM fields Amy collects from creators (by field name).
              </span>
            </FormDescription>
            <FormMessage className="text-xs mt-0" />
          </FormItem>
        )}
      />

      <div className="mt-5">
        <FormLabel>Constants</FormLabel>
        <p className="text-muted-foreground text-xs mt-1 mb-2">
          Define business metrics known upfront (e.g. CVR, LTV, ROAS).
        </p>
        {fields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2 my-2">
            <FormField
              control={form.control}
              name={`${constantsPath}.${index}.name` as any}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input placeholder="Name (e.g. CVR)" {...field} value={(field.value as string) ?? ""} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            =
            <FormField
              control={form.control}
              name={`${constantsPath}.${index}.value` as any}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input
                      placeholder="Value (e.g. 0.1)"
                      type="number"
                      step="any"
                      value={(field.value as number) ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === "" ? undefined : Number(e.target.value)
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="destructive-ghost"
              size="icon-sm"
              onClick={() => remove(index)}
              className="shrink-0"
            >
              <X className="size-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => append({ name: "", value: 0 } as never)}
        >
          <Plus /> Add Constant
        </Button>
      </div>
    </div>
  );
}
